'use client';

import { useAtom, useAtomValue } from 'jotai';
import {
  contentAtom,
  desktopSidebar,
  isLoadingNavAtom,
  isStreamingContentAtom,
  isStreamingMapAtom,
  mapDataAtom,
  mapViewAtom,
  overviewAtom,
} from '../../../../jotaiStore';
import Markdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import GetStartedButton from '../../../../features/nav/GetStartedButton';
import LoadingSpinnerSmall from '../../../../features/global/loading/LoadingSpinnerSmall';
import ViewToggleButton from '../../../../features/nav/ViewToggleButton';
import { ChevronDoubleLeftIcon } from '@heroicons/react/24/outline';
import { cn } from '@/lib/utils';
import { useParams } from 'next/navigation';
import clsx from 'clsx';
import { SidebarClose, SidebarOpen } from 'lucide-react';
import { OVERVIEW_SLUG } from '../../../../constants/constants';
import { useEffect } from 'react';

const loadingSpinner = () => (
  <div className="flex justify-center items-center h-[calc(100vh-55px)] w-full">
    <div className="relative bottom-20 flex flex-col items-center gap-4">
      <LoadingSpinnerSmall className="w-32 h-32" />
      <p className="text-colors-green text-xl font-medium">{`Loading content...`}</p>
    </div>
  </div>
);

const MarkDownContainer = ({
  isNavLoading,
  isRoadMap = false,
  setIsLoading,
}) => {
  
  const params = useParams();
  const [content, setContentData] = useAtom(contentAtom);
  const [mapData, setMapData] = useAtom(mapDataAtom);
  const [overviewContent, setOverview] = useAtom(overviewAtom);
  const isStreamingContent = useAtomValue(isStreamingContentAtom);
  const isStreamingMap = useAtomValue(isStreamingMapAtom);
  const [isLoadingNav, setIsLoadingNav] = useAtom(isLoadingNavAtom);

  const [showDesktopSidebar, setShowDesktopSidebar] = useAtom(desktopSidebar);

  const contentSlug = useParams().contentSlug as string | undefined;

  // Handle initial loading state and content changes
  useEffect(() => {
    if (content || overviewContent) {
      setIsLoadingNav(false);
    }
  }, [content, overviewContent, setIsLoadingNav]);

  // Always show loading spinner on initial render
  if (!content && !overviewContent) {
    return loadingSpinner();
  }

  // Show loading spinner during navigation
  if (isNavLoading) {
    return loadingSpinner();
  }

  return (
    <div className="flex flex-col w-full stretch relative scrollbar scrollbar-thumb-shades-surfaceDark scrollbar-thumb-rounded-full scrollbar-track-rounded-full scrollbar-track-shades-baseDark scrollbar-w-2 mb-[55px] min-h-[calc(100vh-55px)]">
      <div className="relative h-0">
        <div className="sticky top-[17px] left-0 w-full z-[60]">
          <div className="flex justify-between p-4 px-[14px]">
            <button
              type="button"
              className="p-2.5 mt-[2px] hidden text-shades-text shadow-md min-[1000px]:block bg-shades-baseLight hover:bg-shades-baseLightest rounded-lg border-white/25 border-[1px]"
              onClick={() => setShowDesktopSidebar(!showDesktopSidebar)}
            >
              <>
                <span className="sr-only">Hide sidebar</span>
                {showDesktopSidebar ? (
                  <SidebarClose className="w-5 h-5" />
                ) : (
                  <SidebarOpen className="w-5 h-5" />
                )}
              </>
            </button>
          </div>
        </div>
      </div>
      {(content || overviewContent) && (
        <div
          className={clsx(
            'overflow-y-scroll scrollbar scrollbar-thumb-shades-surfaceDark scrollbar-thumb-rounded-full scrollbar-track-rounded-full scrollbar-track-shades-baseDark scrollbar-w-2 ',
            isRoadMap ? 'pt-0' : 'pt-0',
            contentSlug === OVERVIEW_SLUG || contentSlug === undefined
              ? 'max-h-[calc(100vh-58px)]'
              : 'max-h-[calc(100vh-101px)]'
          )}
        >
          <Markdown
            className="prose prose-strong:text-shades-text prose-headings:text-shades-text prose-invert lg:prose-lg  prose-h1:text-colors-green prose-h2:text-colors-green text-shades-textLight prose-p:text-shades-textLight  max-w-[850px] min-[1600px]:max-w-[1000px] px-8 lg:px-12 pb-8 pt-10 md:pt-20 mx-auto "
            rehypePlugins={[rehypeHighlight]}
          >
            {content || overviewContent}
          </Markdown>
          {isStreamingContent && (
            <div className="flex items-center justify-center h-full">
              <LoadingSpinnerSmall />
            </div>
          )}
          <div className="sticky flex justify-end w-full mx-auto mt-8 bottom-10">
            <div className="px-12">
              <GetStartedButton setIsLoading={setIsLoading} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarkDownContainer;
